import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setStatus('error');
      setError(searchParams.get('error_description') || 'Authorization was denied');
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setError('Missing authorization code');
      return;
    }

    // Parse platform from state
    try {
      const decodedState = atob(state);
      const platform = decodedState.split(':')[1];

      // Complete the OAuth flow
      const response = await axios.get(
        `${API_URL}/api/platforms/connect/${platform}/callback`,
        {
          params: { code, state }
        }
      );

      if (response.data.success) {
        setStatus('success');
        toast.success(`Connected to ${response.data.account_label}!`);
        await refreshUser();

        // Check if there's a return asset to go back to
        const returnAssetId = localStorage.getItem('oauth_return_asset');
        localStorage.removeItem('oauth_return_asset');

        setTimeout(() => {
          if (returnAssetId) {
            navigate(`/editor/${returnAssetId}`);
          } else {
            navigate('/library');
          }
        }, 1500);
      } else {
        setStatus('error');
        setError('Failed to connect platform');
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setError(error.response?.data?.detail || 'Failed to complete connection');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl p-8 text-center border border-slate-100 shadow-sm">
          {status === 'processing' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#0a2540] mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                Connecting...
              </h1>
              <p className="text-slate-600">
                Please wait while we complete the connection.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                Connected!
              </h1>
              <p className="text-slate-600">
                Your account has been connected successfully.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                Connection Failed
              </h1>
              <p className="text-slate-600 mb-6">
                {error}
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/library')}
                  variant="outline"
                  className="flex-1"
                >
                  Go to Library
                </Button>
                <Button
                  onClick={() => window.history.back()}
                  className="flex-1 bg-[#0a2540]"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
